import React from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  ShieldCheck,
  Gauge,
  Zap,
  Brain,
  FileText,
  Check,
  X,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { LegalPageLayout, LegalSection } from "../../components/LegalPageLayout";
import { openSubscribeGate } from "../../lib/subscription";
import Seo from "../../components/Seo";

const sections = [
  { id: "gap", label: "The gap most EV apps leave" },
  { id: "degradation", label: "Battery degradation analysis" },
  { id: "warranty", label: "Warranty tracking" },
  { id: "range", label: "Range-loss calculations" },
  { id: "charging", label: "Charging behavior insights" },
  { id: "predictive", label: "Predictive battery health" },
  { id: "reports", label: "Service-ready reports" },
  { id: "vs-tesla", label: "EVIQ vs other EV apps" },
  { id: "who-for", label: "Who is EVIQ for?" },
  { id: "get-started", label: "Get started" },
];

export default function WhyEviq() {
  return (
    <>
      <Seo
        title="Why EVIQ — battery intelligence beyond the Tesla app"
        description="Most EV apps tell you today's state of charge. EVIQ tells you what your battery will do next — degradation velocity, warranty headroom, AI charging advice, and signed Battery Passports from your own Tesla Fleet API telemetry."
        canonical="https://eviq.pro/why-eviq"
      />
      <LegalPageLayout
      eyebrow="Company · Why EVIQ"
      title="Why EVIQ?"
      updated="February 20, 2026"
      intro="Most EV companion apps tell you today's state-of-charge. EVIQ tells you what your battery will do next — and what to do about it while your warranty is still in play. Here's exactly how EVIQ adds value beyond what typical EV apps show you natively."
      sections={sections}
    >
      <LegalSection id="gap" title="1. The gap most EV apps leave">
        <p>
          Most EV companion apps — including manufacturer-issued apps — are
          excellent at what they were designed for: showing you the <em>current</em>{" "}
          state of your vehicle. Today&rsquo;s range, today&rsquo;s charge level,
          today&rsquo;s cabin temperature.
        </p>
        <p>
          What they don&rsquo;t tell you is the <strong>trajectory</strong> — the
          aging curve of the most expensive component in your car. Owners typically
          only learn they have a degradation problem when the projected range has
          dropped 15–25% from new, at which point they may already be past the
          point of the warranty replacement threshold.
        </p>
        <p>
          EVIQ closes that gap by turning telemetry your manufacturer already
          agreed to share (via the official Tesla Fleet API today, more OEMs
          coming) into six analytical products your default EV app doesn&rsquo;t
          provide:
        </p>
      </LegalSection>

      {/* Six pillars */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-14 not-prose">
        <Pillar
          icon={Activity}
          color="#39FF14"
          title="Battery degradation analysis"
          body="30-day slope, 12-month curve, and lifetime trajectory — normalized against every other vehicle of your vintage, climate zone, and driving pattern."
        />
        <Pillar
          icon={ShieldCheck}
          color="#00E5FF"
          title="Warranty tracking"
          body="Climate-adjusted ETA to Tesla's 70% capacity threshold. Know today whether you'll qualify for a warranty replacement — before the warranty window closes."
        />
        <Pillar
          icon={Gauge}
          color="#FFB020"
          title="Range-loss calculations"
          body="What you lost this month, this year, since new. Broken out by cause: cell wear, calibration drift, phantom drain, seasonal temperature, driving pattern."
        />
        <Pillar
          icon={Zap}
          color="#FF6B57"
          title="Charging behavior insights"
          body="Supercharger vs L2 mix, top-off habits, dwell-at-100% frequency — with a personal recommendation to extend pack life without changing your lifestyle."
        />
        <Pillar
          icon={Brain}
          color="#B794F4"
          title="Predictive battery health"
          body="AI-generated narrative that translates the numbers into plain English: what's going on, how urgent it is, and the two or three things worth doing about it."
        />
        <Pillar
          icon={FileText}
          color="#39FF14"
          title="Service-ready reports"
          body="One-tap PDF you can hand to a Tesla service advisor. Anomalies flagged, timelines cited, thresholds referenced — the paper trail a warranty claim needs."
        />
      </div>

      <LegalSection id="degradation" title="2. Battery degradation analysis">
        <p>
          Every EV battery loses capacity as it ages. The question is how fast — and
          whether <em>your</em> pack is aging faster or slower than expected.
        </p>
        <p>EVIQ computes three degradation views:</p>
        <ul>
          <li><strong>Instant slope</strong> — the past 30 days, adjusted for temperature. A slope 4× above the baseline is a red flag we surface as an anomaly.</li>
          <li><strong>12-month curve</strong> — usable-capacity trend line versus your vehicle&rsquo;s day-one EPA capacity, plotted against fleet median for your vintage.</li>
          <li><strong>Lifetime trajectory</strong> — projected State-of-Health at 5, 8, and 10 years, back-tested against thousands of similar vehicles.</li>
        </ul>
      </LegalSection>

      <LegalSection id="warranty" title="3. Warranty tracking">
        <p>
          Tesla&rsquo;s battery warranty replaces packs that fall below <strong>70% of
          original capacity</strong> within 8 years or 100,000–150,000 miles (depending
          on model). Most owners have no idea whether their pack is on track to cross
          that threshold before their warranty expires.
        </p>
        <p>EVIQ shows you three numbers most EV apps don&rsquo;t:</p>
        <ul>
          <li><strong>ETA to 70% crossover</strong> — climate-adjusted, based on your degradation slope.</li>
          <li><strong>Projected State-of-Health at warranty end</strong> — so you know whether you&rsquo;ll qualify.</li>
          <li><strong>Miles-of-headroom</strong> — how many miles you can drive before you burn the warranty window.</li>
        </ul>
        <p>
          If EVIQ projects you&rsquo;ll cross 70% <em>before</em> your warranty expires,
          the app tells you the earliest defensible date to file a claim — with the
          evidence pack ready to print.
        </p>
      </LegalSection>

      <LegalSection id="range" title="4. Range-loss calculations">
        <p>
          &ldquo;Why is my range 40 miles less than when I bought the car?&rdquo; is the
          most common EV owner question — and it almost never has a single answer. EVIQ
          decomposes range loss into five attributable causes:
        </p>
        <ul>
          <li><strong>Cell wear</strong> — irreversible capacity loss from lithium-ion aging.</li>
          <li><strong>Calibration drift</strong> — reversible, corrected by a full 0→100 charge cycle.</li>
          <li><strong>Phantom drain</strong> — vampire loss from Sentry Mode, Cabin Overheat, etc.</li>
          <li><strong>Temperature</strong> — seasonal loss you&rsquo;ll get back next spring.</li>
          <li><strong>Driving pattern</strong> — Chill vs. Sport, average speed, HVAC habits.</li>
        </ul>
        <p>Each category comes with a specific, quantified recommendation.</p>
      </LegalSection>

      <LegalSection id="charging" title="5. Charging behavior insights">
        <p>
          How you charge is the single biggest lever <em>you</em> control over how fast
          your battery ages. EVIQ analyzes your charging history and gives you a
          personal profile:
        </p>
        <ul>
          <li><strong>Supercharger vs Level 2 ratio</strong> vs the fleet median.</li>
          <li><strong>Time spent above 90% SoC</strong> — a known accelerator of aging.</li>
          <li><strong>Nights parked at 100%</strong> — the biggest offender for most owners.</li>
          <li><strong>Precondition-in-winter compliance</strong> — a common Model S/X blind spot.</li>
          <li><strong>Optimal charge window</strong> — matched to your utility&rsquo;s off-peak schedule.</li>
        </ul>
      </LegalSection>

      <LegalSection id="predictive" title="6. Predictive battery health analytics">
        <p>
          EVIQ Intelligence™ produces a natural-language narrative for your pack: what
          the anomaly detector saw, what it means, and the two or three actions worth
          taking. Every claim in the narrative links back to a specific number on your
          dashboard — nothing is invented.
        </p>
        <p>
          The AI is a translation layer over deterministic math — not the other way
          around. You always see the numbers underneath.
        </p>
      </LegalSection>

      <LegalSection id="reports" title="7. Service-ready reports">
        <p>
          Every subscriber can generate a one-tap PDF designed for a Tesla service
          advisor. It includes:
        </p>
        <ul>
          <li>Your VIN, model, and current EVIQ Score.</li>
          <li>State-of-Health with date-stamped measurement history.</li>
          <li>Flagged anomalies, plotted against the Tesla warranty threshold.</li>
          <li>Projected 70% crossover date and its confidence interval.</li>
          <li>Citations to the underlying telemetry timestamps.</li>
        </ul>
        <p>
          Owners who&rsquo;ve used the report say it converts &ldquo;my range feels
          off&rdquo; into a service-advisor conversation that ends with a work order.
        </p>
      </LegalSection>

      {/* Comparison table */}
      <LegalSection id="vs-tesla" title="8. EVIQ vs other EV apps">
        <p>
          Not a replacement — a complement. Here&rsquo;s the honest breakdown:
        </p>
        <p>
          Want the named-rival detail? Read the dedicated breakdowns:{" "}
          <a href="/vs/teslita">EVIQ vs Teslita</a> (free snapshot checks vs tracked
          degradation) and <a href="/vs/recurrent">EVIQ vs Recurrent</a> (owner
          telemetry vs shopper-side data).
        </p>
        <div className="not-prose overflow-x-auto -mx-4 sm:mx-0 border border-white/10 rounded-xl bg-white/[0.02]">
          <table className="w-full text-sm min-w-[520px]" data-testid="why-eviq-comparison">
            <thead>
              <tr className="border-b border-white/10 text-left text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                <th className="px-4 py-3">Capability</th>
                <th className="px-4 py-3 text-center">Other EV apps</th>
                <th className="px-4 py-3 text-center">EVIQ</th>
              </tr>
            </thead>
            <tbody className="text-zinc-300">
              <ComparisonRow label="Today's state of charge" tesla={true} eviq={true} />
              <ComparisonRow label="Vehicle controls (unlock, precondition, honk)" tesla={true} eviq={false} note="Read-only by design" />
              <ComparisonRow label="Trip planning across the charging network" tesla={true} eviq={true} />
              <ComparisonRow label="EVIQ Score™ (0–100 pack health composite)" tesla={false} eviq={true} />
              <ComparisonRow label="Battery Life Predictor (years-of-life-remaining forecast)" tesla={false} eviq={true} />
              <ComparisonRow label="30-day degradation slope" tesla={false} eviq={true} />
              <ComparisonRow label="12-month degradation curve vs fleet median" tesla={false} eviq={true} />
              <ComparisonRow label="Warranty-threshold ETA (climate-adjusted)" tesla={false} eviq={true} />
              <ComparisonRow label="5-class anomaly detection (phantom drain, cell imbalance, thermal, calibration, precondition)" tesla={false} eviq={true} />
              <ComparisonRow label="Range-loss decomposition by cause" tesla={false} eviq={true} />
              <ComparisonRow label="Charging behavior benchmark vs fleet" tesla={false} eviq={true} />
              <ComparisonRow label="AI natural-language pack narrative" tesla={false} eviq={true} />
              <ComparisonRow label="Service-ready warranty PDF" tesla={false} eviq={true} />
              <ComparisonRow label="Cell health map" tesla={false} eviq={true} />
              <ComparisonRow label="Marketplace resale value with degradation adjustment" tesla={false} eviq={true} />
            </tbody>
          </table>
        </div>
        <p className="text-xs text-zinc-500 font-mono mt-3">
          &ldquo;Other EV apps&rdquo; refers to the typical manufacturer or third-party
          companion apps EV owners use day-to-day. EVIQ is independent and not affiliated
          with any vehicle manufacturer.
        </p>
      </LegalSection>

      <LegalSection id="who-for" title="9. Who is EVIQ for?">
        <ul>
          <li><strong>Long-term owners</strong> who plan to keep the vehicle past year 5.</li>
          <li><strong>Owners approaching warranty end</strong> who want to know whether they qualify for a replacement.</li>
          <li><strong>Owners about to sell or trade</strong> who want an independent, third-party health report to defend their price.</li>
          <li><strong>High-Supercharger users</strong> who want to understand what their charging profile is costing them.</li>
          <li><strong>Owners in extreme climates</strong> — Phoenix summers, Minnesota winters — where degradation math is very different from Bay Area norms.</li>
        </ul>
      </LegalSection>

      <LegalSection id="get-started" title="10. Get started">
        <p>
          Preview the whole app for free — no sign-up required. See what your dashboard
          would look like, then start a 7-day trial when you&rsquo;re ready to connect
          your Tesla.
        </p>
        <div className="not-prose flex flex-col sm:flex-row gap-3 mt-5">
          <Link
            to="/app"
            data-testid="why-eviq-preview-cta"
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 hover:border-white/30 bg-white/[0.03] hover:bg-white/[0.06] px-5 py-3 text-sm font-medium text-white transition-colors"
          >
            Preview the app
            <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
          </Link>
          <button
            type="button"
            onClick={() => openSubscribeGate("why-eviq-page")}
            data-testid="why-eviq-trial-cta"
            className="inline-flex items-center justify-center gap-2 rounded-xl brand-gradient px-5 py-3 text-sm font-semibold text-black hover:opacity-90 transition-opacity"
          >
            <Sparkles className="w-4 h-4" strokeWidth={2.5} />
            Start 7-day free trial
          </button>
        </div>
      </LegalSection>
    </LegalPageLayout>
    </>
  );
}

const Pillar = ({ icon: Icon, color, title, body }) => (
  <div
    className="rounded-2xl border p-5 hover:bg-white/[0.02] transition-colors"
    style={{ borderColor: `${color}30`, background: `${color}0A` }}
  >
    <div
      className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 border"
      style={{ borderColor: `${color}40`, background: `${color}18` }}
    >
      <Icon className="w-4 h-4" style={{ color }} strokeWidth={2} />
    </div>
    <h3 className="text-white font-semibold text-base mb-2 tracking-tight">{title}</h3>
    <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
  </div>
);

const ComparisonRow = ({ label, tesla, eviq, note }) => (
  <tr className="border-b border-white/5 last:border-0">
    <td className="px-4 py-3 text-[13px]">
      {label}
      {note && <div className="text-[11px] text-zinc-500 mt-0.5 font-mono">{note}</div>}
    </td>
    <td className="px-4 py-3 text-center">
      {tesla ? (
        <Check className="w-4 h-4 mx-auto" style={{ color: "#39FF14" }} strokeWidth={3} />
      ) : (
        <X className="w-4 h-4 mx-auto" style={{ color: "#3f3f46" }} strokeWidth={2.5} />
      )}
    </td>
    <td className="px-4 py-3 text-center">
      {eviq ? (
        <Check className="w-4 h-4 mx-auto" style={{ color: "#39FF14" }} strokeWidth={3} />
      ) : (
        <X className="w-4 h-4 mx-auto" style={{ color: "#3f3f46" }} strokeWidth={2.5} />
      )}
    </td>
  </tr>
);
